<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import AdoptWork from './components/AdoptWork.vue';
import { account, restore } from './lib/auth';
import { installBuiltins } from './lib/builtin-books';
import { saveNow, syncable, watchNetwork } from './lib/cloudwork';
import {
  claim, decline, enter, lastActive, ownerId, unclaimedWork, wasOffered,
} from './lib/workspaces';
import { chaptersOf, load, useWorkspace } from './lib/workspace';
import { reopenProfile } from './lib/profile';
import { loadTeams, reopenTeams } from './lib/teamspace';

/**
 * The paper theme is applied before the first route renders, so no page flashes the wrong
 * ground on the way in.
 */
const stored = (() => {
  try { return localStorage.getItem('euclid.paper'); } catch { return null; }
})();
if (stored === 'light' || stored === 'dark') document.documentElement.dataset.theme = stored;

const { courses } = useWorkspace();

/**
 * Which chapter a built-in book belongs in, resolved on the device.
 *
 * Chapter ids are minted per device, so a shipped book cannot carry one. It carries an exercise
 * RANGE instead, and the chapter that contains it is looked up here — the same way the app decides
 * which chapter an exercise number belongs to everywhere else. No match is not a failure: the book
 * lands in the library unattached, which is exactly what an uploaded PDF does when it is opened
 * straight onto a scratch sheet.
 */
const chapterFor = (book: { from: number; to: number }) => {
  for (const course of courses.value) {
    const found = chaptersOf(course.id).find(ch => ch.from <= book.from && book.to <= ch.to);
    if (found) return { chapterId: found.id, courseId: course.id };
  }
  return null;
};

/**
 * The two chapters install themselves, after first paint and never before it.
 *
 * Extracting a 47-page PDF is seconds of worker time, so this may not be anywhere near the boot
 * path — `PLAN.md`'s local-first rule is about the first frame, and a book arriving a moment later
 * is invisible. Nothing waits on it and nothing breaks if it fails.
 */
function installBooksWhenIdle(): void {
  const go = () => { void installBuiltins({ chapterFor }); };
  if ('requestIdleCallback' in window) {
    (window as Window & { requestIdleCallback: (cb: () => void, o?: { timeout: number }) => void })
      .requestIdleCallback(go, { timeout: 4000 });
    return;
  }
  // WebKit has no requestIdleCallback. A timeout is the honest fallback: still off the boot path.
  setTimeout(go, 1200);
}

/**
 * Whose sheets these are, decided before any of them is read.
 *
 * > *"new log in = another account = ONLY THEIR SAVED STUFF SHOWN"*
 *
 * The workspace is a database name and `lib/workspaces.ts` picks it. Two calls, in this order and
 * for two different reasons:
 *
 * 1. **Before `load()`**, from what was written down last time. Confirming a session is a round
 *    trip and a local-first app does not wait on one, so the opening guess is the last answer.
 * 2. **When the session resolves, and on every change after it.** If the guess was wrong — a
 *    session expired overnight, or somebody signed in on another tab — the pointer moves and
 *    everything read out of the old database is read again.
 *
 * `enter` returns whether it actually moved, so the common case costs one comparison.
 */
async function follow(): Promise<void> {
  if (!await enter(ownerId(account.value))) return;

  /*
   * Everything an identity owns is reopened together.
   *
   * The work was scoped first and the other two were missed, which from the outside looked like
   * the account had not switched at all: a second address showed the first person's display name,
   * their portrait and their teams. They are three databases and one question — whose device is
   * this right now — so they move as one.
   */
  await Promise.all([
    reopenProfile().catch(() => { /* the profile falls back to its defaults */ }),
    reopenTeams().catch(() => { /* teams reload on the next visit to the surface */ }),
  ]);
  try { await load(); } catch (error) { console.error('The workspace could not be loaded', error); }
  try { await loadTeams(); } catch { /* the teams page loads its own on arrival */ }
}

/**
 * The offer to keep this device's existing work, made once and only when there is work.
 *
 * The count is read before the question is put, because a dialog that says "you have 0 records
 * here" is a dialog nobody should have been shown.
 */
const offering = ref<{ email: string; records: number } | null>(null);

async function offerAdoption(): Promise<void> {
  const who = account.value;
  if (!who || who.anonymous || wasOffered(who.id)) return;
  const records = await unclaimedWork();
  if (records === 0) return;
  offering.value = { email: who.email, records };
}

async function keepWork(): Promise<void> {
  const who = account.value;
  offering.value = null;
  if (!who) return;
  claim(who.id);
  // Claiming changes which database this account resolves to, so the pointer moves and everything
  // in memory is read again — out of the database that was here all along.
  if (await enter(ownerId(account.value))) {
    try { await load(); } catch (error) { console.error('The workspace could not be loaded', error); }
  }
  /*
   * And up it goes, now rather than on the next stroke.
   *
   * A claim writes no record, so nothing fires the ticker — an evening of work would sit here
   * until the next edit, which for a device somebody is about to close is never. This is the one
   * press where "saved to the account" has to be true immediately, because it is the press that
   * says the work belongs to the account.
   */
  void saveNow();
}

function startSeparately(): void {
  const who = account.value;
  offering.value = null;
  if (who) decline(who.id);
}

watch(account, () => { void follow().then(offerAdoption); });

onMounted(async () => {
  // The install is not downstream of the workspace loading. It was written as `await load()` and
  // then install, which quietly made a failure to open the spine into a device with no chapters
  // on it — two unrelated things sharing a fate for no reason other than the order they were
  // written in.
  await enter(lastActive());
  try { await load(); } catch (error) { console.error('The workspace could not be loaded', error); }
  installBooksWhenIdle();

  // The session, after the first paint and after the sheets are on screen. It confirms the guess
  // above far more often than it corrects it.
  await restore();
  await follow();
  await offerAdoption();

  /*
   * The ticker, last of all.
   *
   * It listens for writes and for the network coming back; the first pass is what pulls an
   * evening done on the other machine onto this one. Nothing waits on it — a sheet is already
   * open and drawable by the time this line runs.
   */
  watchNetwork();
  if (syncable.value) void saveNow();
});
</script>

<template>
  <RouterView />

  <AdoptWork
    v-if="offering" :email="offering.email" :records="offering.records"
    @keep="keepWork" @separate="startSeparately"
  />
</template>
